/* eslint-disable valid-jsdoc */
import { RSAA } from 'redux-api-middleware';
import { get, isEmpty } from 'lodash';

import endpoints from '../../utils/endpoints';
import { addNotification } from '../../states/notifications/actions';
import { goToManagePayeePage } from '../../states/api/goToManagePayeePage';
import { getBankId } from '../../states/session/selectors';
import { MULTI_BENEFICIARY_CREATE_REQUEST } from './MULTI_BENEFICIARY_REQUEST';
import {
	handleImportPayeesPartialSuccess,
	handleImportPayeesNoSuccess,
	getSuccessDataListCount,
	getFormattedDataFromApiResponse,
} from '../../utils/benImportUtils';
import {
	getBacsPaymentsToAdd,
	getPayeesFromImportedPaymentsList,
	getFPSorCHAPSPaymentsToAdd,
	getFormattedFailedPayments,
} from '../../utils/paymentsImportUtils';
import { getImportedPaymentList } from '../../states/importedPaymentList/selector';
import { addMultiplePayments } from '../../states/payments/actions';
import { chapsAddMultiplePayments } from '../../states/chaps/chapsAddMultiplePayments';
import { TYPE_BACS_CREDIT, TYPE_FASTER_PAYMENTS } from '../../config';

import { getSelectedPayeeId, getSelectedPayeeName } from './selectors';
import { getPage } from '../../states/accountPaging/actions';
import { getCurrentPageInfo } from '../../states/accountPaging/selectors';
import {
	getBeneficiaryCreateRequestForFileImport,
	getBeneficiaryCreateRequestForFileImportWithFromAccount,
} from '../../helpers/apiRequestBuilder/beneficiary';
import { getChunkingInfo } from '../../states/api/chunkingActions';
import { submitMultiPartChunkingRequest } from '../../states/api/submitMultiPartChunkingRequest';

export const SET_SELECTED_PAYEE = 'SET_SELECTED_PAYEE';
export const SUBMIT_DELETE_PAYEE_REQUEST = 'SUBMIT_DELETE_PAYEE_REQUEST';
export const SUBMIT_DELETE_PAYEE_SUCCESS = 'SUBMIT_DELETE_PAYEE_SUCCESS';
export const SUBMIT_DELETE_PAYEE_FAILURE = 'SUBMIT_DELETE_PAYEE_FAILURE';
export const SET_EDIT_PAYEE_FLAG = 'SET_EDIT_PAYEE_FLAG';
export const RESET_MANAGE_PAYEE = 'RESET_MANAGE_PAYEE';
export const SET_PAYEE_TYPE = 'SET_PAYEE_TYPE';

export const setSelectedPayee = (selectedPayee) => ({
	type: SET_SELECTED_PAYEE,
	selectedPayee,
});

export const setEditPayeeFlag = () => ({
	type: SET_EDIT_PAYEE_FLAG,
});

export const resetManagePayee = () => ({
	type: RESET_MANAGE_PAYEE,
});

export const setPayeeType = (payeeType) => ({
	type: SET_PAYEE_TYPE,
	payeeType,
});

export const submitDeletePayeeRequest = (sourceAccountId) => (dispatch, getState) => {
	const state = getState();
	const bankId = getBankId(state);
	const endpoint = endpoints.deletePayee;
	const selectedPayeeId = getSelectedPayeeId(state);
	const uri = endpoint.resolve(bankId, sourceAccountId, null, selectedPayeeId);
	const action = {
		[RSAA]: {
			endpoint: uri,
			method: endpoint.method,
			types: [
				SUBMIT_DELETE_PAYEE_REQUEST,
				{
					type: SUBMIT_DELETE_PAYEE_SUCCESS,
					meta: {
						selectedPayeeId,
					},
				},
				SUBMIT_DELETE_PAYEE_FAILURE,
			],
			headers: {
				'x-bpi-version': endpoint.x_bpi_version,
			},
		},
	};
	return dispatch(action);
};

export const submitDeletePayee =
	({ sourceAccountId, deletePayeeSuccessMessage, closeDeleteConfirmationModal }) =>
	(dispatch, getState) => {
		const state = getState();
		const selectedPayeeName = getSelectedPayeeName(state);
		const { currentPage } = getCurrentPageInfo(state);
		return dispatch(submitDeletePayeeRequest(sourceAccountId)).then((response) => {
			if (response.type === SUBMIT_DELETE_PAYEE_SUCCESS) {
				dispatch(
					addNotification('deletePayee', {
						selectedPayeeName,
						deletePayeeSuccessMessage,
					})
				);
				closeDeleteConfirmationModal();
				dispatch(goToManagePayeePage());
				dispatch(resetManagePayee());
				dispatch(getPage(currentPage));
			}
			return response;
		});
	};

export const MULTI_BENEFICIARY_CREATE_SUCCESS = 'MULTI_BENEFICIARY_CREATE_SUCCESS';
export const MULTI_BENEFICIARY_CREATE_FAILURE = 'MULTI_BENEFICIARY_CREATE_FAILURE';

const handleImportPayeesResponse = (successMessage, goBackPage, importedPayeesList, response) => (dispatch) => {
	if (response && response.type === MULTI_BENEFICIARY_CREATE_SUCCESS) {
		const formattedData = getFormattedDataFromApiResponse(response, importedPayeesList);
		if (getSuccessDataListCount(formattedData) > 0) {
			handleImportPayeesPartialSuccess(formattedData, successMessage, addNotification)(dispatch);
		} else if (getSuccessDataListCount(formattedData) === 0) {
			handleImportPayeesNoSuccess(formattedData, addNotification)(dispatch);
		}
	} else if (response && response.type === MULTI_BENEFICIARY_CREATE_FAILURE) {
		dispatch(
			addNotification('backendBenImportError', {
				contentKey: 'serverError',
				errorType: 'failure',
				isServerValidation: true,
			})
		);
	}
	goBackPage();
};

const multiBeneficiaryActionTypes = [
	MULTI_BENEFICIARY_CREATE_REQUEST,
	MULTI_BENEFICIARY_CREATE_SUCCESS,
	MULTI_BENEFICIARY_CREATE_FAILURE,
];

export const submitImportPayeesRequest =
	({ successMessage, goBackPage, importedPayeesList, accountsForAddPayee }) =>
	(dispatch, getState) => {
		const state = getState();
		const bankId = getBankId(state);
		const isSingleUse = false;
		const accountForBeneficiaries = get(accountsForAddPayee, '[0]', {});
		const payloadForChunking = importedPayeesList.map(
			getBeneficiaryCreateRequestForFileImport(bankId, accountForBeneficiaries.id, isSingleUse)
		);
		const { totalChunks, chunks, totalItems } = getChunkingInfo(payloadForChunking, state);
		return dispatch(
			submitMultiPartChunkingRequest(
				multiBeneficiaryActionTypes,
				totalChunks,
				chunks,
				totalItems,
				isSingleUse,
				accountForBeneficiaries
			)
		).then((response) =>
			handleImportPayeesResponse(successMessage, goBackPage, importedPayeesList, response)(dispatch)
		);
	};

export const submitImportedPayeesForBacsPaymentRequest =
	({ importedPaymentsData, accountsForAddPayee, addMultiplePayments, handleContinue, handleBack, type }) =>
	(dispatch, getState) => {
		const state = getState();
		const accountForTemporaryBeneficiaries = get(accountsForAddPayee, '[0]', {});
		const hasValidAccount = !isEmpty(accountForTemporaryBeneficiaries);
		if (hasValidAccount) {
			const bankId = getBankId(state);
			const isSingleUse = true; // one-off payment
			const isCreditData = type === TYPE_BACS_CREDIT;
			const importedPayeesList = getPayeesFromImportedPaymentsList(importedPaymentsData, isCreditData, isSingleUse);
			const payloadForChunking = importedPayeesList.map(
				getBeneficiaryCreateRequestForFileImport(bankId, accountForTemporaryBeneficiaries.id, isSingleUse)
			);
			const { totalChunks, chunks, totalItems } = getChunkingInfo(payloadForChunking, state);
			dispatch(
				submitMultiPartChunkingRequest(
					multiBeneficiaryActionTypes,
					totalChunks,
					chunks,
					totalItems,
					isSingleUse,
					accountForTemporaryBeneficiaries
				)
			).then((response) => {
				if (response.type === MULTI_BENEFICIARY_CREATE_SUCCESS) {
					const responses = get(response, 'payload.apiResponses');
					const updatedPaymentsData = getBacsPaymentsToAdd(responses, importedPaymentsData, isCreditData);
					addMultiplePayments(updatedPaymentsData);
					handleContinue();
				} else if (response && response.type === MULTI_BENEFICIARY_CREATE_FAILURE && response.error) {
					dispatch(
						addNotification('serviceErrorDuringPaymentUpload', {
							errorType: 'genericServiceError',
						})
					);
					handleBack();
				} else if (response && response.type === MULTI_BENEFICIARY_CREATE_FAILURE && !response.error) {
					const responses = get(response, 'payload.apiResponses');
					const formattedFailedPayments = getFormattedFailedPayments(responses, importedPaymentsData);
					dispatch(
						addNotification('backendBenCheckingPartialSuccessWithError', {
							contentKey: isCreditData ? 'checkingPayeesServerErrorBACSCredit' : 'checkingPayeesServerErrorBACS',
							formattedData: formattedFailedPayments,
							errorType: 'userDataError',
							isServerValidation: true,
						})
					);
					handleBack();
				}
			});
		} else {
			dispatch(
				addNotification('permissionError', {
					errorType: 'permissionError',
				})
			);
			handleBack();
		}
	};

export const submitImportedPaymentRequestForFpsOrChaps =
	({ accountsForAddPayee, handleContinue, handleBack, type }) =>
	(dispatch, getState) => {
		const state = getState();
		const importedPaymentsData = getImportedPaymentList(state);
		const accountForTemporaryBeneficiaries = get(accountsForAddPayee, '[0]', {});
		const hasValidAccount = !isEmpty(accountForTemporaryBeneficiaries);

		if (hasValidAccount) {
			const bankId = getBankId(state);
			const isProcessingFPS = type === TYPE_FASTER_PAYMENTS;
			const isSingleUse = true;
			const importedPayeesList = getPayeesFromImportedPaymentsList(importedPaymentsData, true, isSingleUse);
			const payloadForChunking = importedPayeesList.map(
				getBeneficiaryCreateRequestForFileImportWithFromAccount(bankId, isSingleUse)
			);
			const { totalChunks, chunks, totalItems } = getChunkingInfo(payloadForChunking, state);
			dispatch(
				submitMultiPartChunkingRequest(
					multiBeneficiaryActionTypes,
					totalChunks,
					chunks,
					totalItems,
					isSingleUse,
					accountForTemporaryBeneficiaries
				)
			).then((response) => {
				if (response.type === MULTI_BENEFICIARY_CREATE_SUCCESS) {
					const responses = get(response, 'payload.apiResponses');
					const updatedPaymentsData = getFPSorCHAPSPaymentsToAdd(responses, importedPaymentsData);
					if (isProcessingFPS) {
						dispatch(addMultiplePayments(updatedPaymentsData));
					} else {
						dispatch(chapsAddMultiplePayments(updatedPaymentsData));
					}
					handleContinue();
				} else if (response && response.type === MULTI_BENEFICIARY_CREATE_FAILURE && response.error) {
					dispatch(
						addNotification('serviceErrorDuringPaymentUpload', {
							errorType: 'genericServiceError',
						})
					);
					handleBack();
				} else if (response && response.type === MULTI_BENEFICIARY_CREATE_FAILURE && !response.error) {
					const responses = get(response, 'payload.apiResponses');
					const formattedFailedPayments = getFormattedFailedPayments(responses, importedPaymentsData);
					dispatch(
						addNotification('backendBenCheckingPartialSuccessWithError', {
							contentKey: 'checkingPayeesServerErrorFPSCHAPS',
							formattedData: formattedFailedPayments,
							errorType: 'userDataError',
							isServerValidation: true,
						})
					);
					handleBack();
				}
			});
		} else {
			dispatch(
				addNotification('permissionError', {
					errorType: 'permissionError',
				})
			);
			handleBack();
		}
	};
